import React, { useState } from 'react'

import { ColorSearchOptions } from 'services/apiTypes/card.types'

import ManaSymbol from 'components/misc/customIcons/ManaSymbol'

import styles from './colorToggles.module.scss'

type Props = {
  value?: ColorSearchOptions
  stringValue?: string

  onChange?: (values: ColorSearchOptions) => void
  onStringValueChange?: (updatedColorString: string) => void

  className?: string
}

const ColorToggles = ({ value, stringValue, onChange, onStringValueChange, className = '' }: Props) => {
  const getColorValue = (valueProperty: string, colorChar: string): boolean | undefined => {
    if (stringValue !== undefined) return stringValue?.toLocaleLowerCase().includes(colorChar)

    // @ts-ignore - if the value isn't present use the fallback value
    if (value !== undefined) return value[valueProperty]

    return undefined
  }

  const [White, setWhite] = useState<boolean>(getColorValue('White', 'w') ?? true)
  const [Blue, setBlue] = useState<boolean>(getColorValue('Blue', 'u') ?? true)
  const [Black, setBlack] = useState<boolean>(getColorValue('Black', 'b') ?? true)
  const [Red, setRed] = useState<boolean>(getColorValue('Red', 'r') ?? true)
  const [Green, setGreen] = useState<boolean>(getColorValue('Green', 'g') ?? true)
  const [Colorless, setColorless] = useState<boolean>(getColorValue('Colorless', 'c') ?? true)

  const handleToggleWhite = () => {
    setWhite(!White)
    handleChange({ White: !(getColorValue('White', 'w') ?? White) })
  }

  const handleToggleBlue = () => {
    setBlue(!Blue)
    handleChange({ Blue: !(getColorValue('Blue', 'u') ?? Blue) })
  }

  const handleToggleBlack = () => {
    setBlack(!Black)
    handleChange({ Black: !(getColorValue('Black', 'b') ?? Black) })
  }

  const handleToggleRed = () => {
    setRed(!Red)
    handleChange({ Red: !(getColorValue('Red', 'r') ?? Red) })
  }

  const handleToggleGreen = () => {
    setGreen(!Green)
    handleChange({ Green: !(getColorValue('Green', 'g') ?? Green) })
  }

  const handleToggleColorless = () => {
    setColorless(!Colorless)
    handleChange({ Colorless: !(getColorValue('Colorless', 'c') ?? Colorless) })
  }

  const handleChange = (updates: Partial<ColorSearchOptions>) => {
    const latest = {
      White: value?.White ?? White,
      Blue: value?.Blue ?? Blue,
      Black: value?.Black ?? Black,
      Red: value?.Red ?? Red,
      Green: value?.Green ?? Green,
      Colorless: value?.Colorless ?? Colorless,

      ...updates,
    }

    if (onChange) onChange(latest)

    if (onStringValueChange) {
      let colors = ''

      if (latest.White) colors += 'W'
      if (latest.Blue) colors += 'U'
      if (latest.Black) colors += 'B'
      if (latest.Red) colors += 'R'
      if (latest.Green) colors += 'G'
      if (latest.Colorless && !colors) colors += 'C'

      onStringValueChange(colors)
    }
  }

  return (
    <div className={`${styles.container} ${className}`}>
      <button type="button" onClick={handleToggleWhite} style={{ opacity: value?.White ?? White ? '1' : '0.2' }}>
        <ManaSymbol symbol="W" height="2em" width="2em" />
      </button>
      <button type="button" onClick={handleToggleBlue} style={{ opacity: value?.Blue ?? Blue ? '1' : '0.2' }}>
        <ManaSymbol symbol="U" height="2em" width="2em" />
      </button>
      <button type="button" onClick={handleToggleBlack} style={{ opacity: value?.Black ?? Black ? '1' : '0.2' }}>
        <ManaSymbol symbol="B" height="2em" width="2em" />
      </button>
      <button type="button" onClick={handleToggleRed} style={{ opacity: value?.Red ?? Red ? '1' : '0.2' }}>
        <ManaSymbol symbol="R" height="2em" width="2em" />
      </button>
      <button type="button" onClick={handleToggleGreen} style={{ opacity: value?.Green ?? Green ? '1' : '0.2' }}>
        <ManaSymbol symbol="G" height="2em" width="2em" />
      </button>

      <button
        type="button"
        onClick={handleToggleColorless}
        style={{ opacity: value?.Colorless ?? Colorless ? '1' : '0.2' }}>
        <ManaSymbol symbol="C" height="2em" width="2em" />
      </button>
    </div>
  )
}

export default ColorToggles

type RadioProps = {
  value?: boolean
  onChange: (value: boolean) => void
}

export const ColorAndOrRadio = ({ value, onChange }: RadioProps) => {
  const [localValue, setLocalValue] = useState<boolean>(value ?? false)

  const orLogic = 'or-logic'
  const andLogic = 'and-logic'

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value === andLogic)
    onChange(e.target.value === andLogic)
  }
  return (
    <div className={styles.radioContainer}>
      <label className={styles.mainRadioLabel}>Logic:</label>
      <span className={styles.cursors}>
        <input
          onChange={handleChange}
          name={orLogic}
          value={orLogic}
          type="radio"
          id={orLogic}
          checked={!(value ?? localValue)}
        />
        <label htmlFor={orLogic}>Or</label>
        <input
          onChange={handleChange}
          name={andLogic}
          value={andLogic}
          type="radio"
          id={andLogic}
          checked={value ?? localValue}
        />
        <label htmlFor={andLogic}>And</label>
      </span>
    </div>
  )
}

export const ColorIdentityRadio = ({ value, onChange }: RadioProps) => {
  const [localValue, setLocalValue] = useState<boolean>(value ?? false)

  const colorName = 'logic-color'
  const identityName = 'identity-color'

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value === identityName)
    onChange(e.target.value === identityName)
  }

  return (
    <div className={styles.radioContainer}>
      <label className={styles.mainRadioLabel}>Options:</label>
      <span className={styles.cursors}>
        <input
          onChange={handleChange}
          name={colorName}
          value={colorName}
          type="radio"
          id={colorName}
          checked={!(value ?? localValue)}
        />
        <label htmlFor={colorName}>Color</label>
        <input
          onChange={handleChange}
          name={identityName}
          value={identityName}
          type="radio"
          id={identityName}
          checked={value ?? localValue}
        />
        <label htmlFor={identityName}>Identity</label>
      </span>
    </div>
  )
}
